function newPlayerStats(name: string): PlayerStats {
    return {
        name,
        gamesServed: 0,
        serves: 0,
        breaks: 0,
        holds: 0,
        firstFaults: 0,
        secondFaults: 0,
        forehand: { win: 0, lose: 0 },
        backhand: { win: 0, lose: 0 },
        volley: { win: 0, lose: 0 },
        screenLob: { win: 0, lose: 0 },
        overhead: { win: 0, lose: 0 },
        winners: 0,
        losers: 0,
    };
}

function getTeam(
    player: string,
    players: [[string, string], [string, string]],
): 0 | 1 {
    return players[0].includes(player) ? 0 : 1;
}

export default function calculateStats(summary: Summary) {
    const totalPoints = summary.points.length;
    const playerStats = summary.players
        .flatMap((team) => team.map((player) => player))
        .reduce((acc: Record<string, PlayerStats>, player) => {
            acc[player] = newPlayerStats(player);
            return acc;
        }, {});

    const games: Game[] = [];
    let gameStartPoint: number | null = null;

    for (let i = 0; i < totalPoints; i += 1) {
        const point = summary.points[i];

        const serveTeam = point.serve;
        const servePlayerStats = playerStats[point.serve_player];

        const outcomePlayerStats = playerStats[point.outcome.player];
        const outcomeTeam = getTeam(point.outcome.player, summary.players);
        const { result, shot } = point.outcome;

        servePlayerStats.serves += 1;

        // Serve stats
        if (result === "Fault" && serveTeam === outcomeTeam) {
            const previousPoint = summary.points[i - 1];
            if (point.serve_player === previousPoint?.serve_player && previousPoint?.outcome.result === "Fault") {
                servePlayerStats.firstFaults -= 1;
                servePlayerStats.secondFaults += 1;
            } else {
                servePlayerStats.firstFaults += 1;
            }
        }

        // Outcome stats
        switch (shot) {
            case "Forehand":
                outcomePlayerStats.forehand[result === "W" ? "win" : "lose"] += 1;
                break;
            case "Backhand":
                outcomePlayerStats.backhand[result === "W" ? "win" : "lose"] += 1;
                break;
            case "Volley":
                outcomePlayerStats.volley[result === "W" ? "win" : "lose"] += 1;
                break;
            case "Screen":
            case "Lob":
                outcomePlayerStats.screenLob[result === "W" ? "win" : "lose"] += 1;
                break;
            case "Overhead":
                outcomePlayerStats.overhead[result === "W" ? "win" : "lose"] += 1;
                break;
            default:
                break;
        }

        // Calculate games
        const { score } = point;
        const nextScore = summary.points[i + 1]?.score;
        const gameEnd = score[0].some((s) => s === 40)
            && nextScore && nextScore[0].every((s) => s === 0);
        if (gameStartPoint === null && score[0].every((s) => s === 0)) {
            gameStartPoint = i;
        } else if (gameStartPoint !== null && gameEnd) {
            // Determine winner
            let winner: 0 | 1 | null = null;
            for (let j = 1; j < score.length; j += 1) {
                if (score[j][0] !== nextScore[j][0]) {
                    winner = 0;
                    break;
                } else if (score[j][1] !== nextScore[j][1]) {
                    winner = 1;
                    break;
                }
            }

            games.push({
                serverName: point.serve_player,
                startPoint: gameStartPoint,
                winner,
                endPoint: i,
            });
            servePlayerStats.gamesServed += 1;
            if (serveTeam === winner) {
                servePlayerStats.holds += 1;
            } else {
                servePlayerStats.breaks += 1;
            }
            gameStartPoint = null;
        } else if (gameStartPoint !== null && i === totalPoints - 1) {
            games.push({
                serverName: point.serve_player,
                startPoint: gameStartPoint,
                winner: null,
                endPoint: null,
            });
            servePlayerStats.gamesServed += 1;
        }

        // Overall stats
        if (result === "W") {
            outcomePlayerStats.winners += 1;
        } else if (result === "L") {
            outcomePlayerStats.losers += 1;
        }

        playerStats[point.serve_player] = servePlayerStats;
        playerStats[point.outcome.player] = outcomePlayerStats;
    }

    const finishedGames = games.filter((g) => g.winner !== null);
    const teamStats = [0, 1].reduce((acc: Record<number, TeamStats>, team) => {
        acc[team] = {
            played: finishedGames.length,
            won: finishedGames.filter((g) => g.winner === team).length,
        };
        return acc;
    }, {});

    return {
        totalPoints,
        games,
        teamStats,
        playerStats,
    };
}
